import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getRouteMutationKey } from '.';
import { routeService } from '../../../../application/RouteService';
import { ROUTE_STATUS } from '../../../../constants/Routes';
import { STOP_STATUS } from '../../../../constants/Stops';
import { Route } from '../../../../domain/route/Route';
import { RouteStatus } from '../../../../domain/route/RouteStatus';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { getLastUnfinishedRouteMutationKey } from '../../home/hooks';
import { getUnfinishedRoutesMutationKey } from '../../routes-list/hooks';
import { RouteActionType } from '../context/actions';
import { useRouteContext } from '../context/context';

interface UseUpdateRouteStatusProps {
  driverRoute: Route;
}

type UseUpdateRouteStatusResponseProps = ServiceResponse<Function, void>;

// Hooks act as 'Adapter' layer.
export const useUpdateRouteStatus = ({ driverRoute }: UseUpdateRouteStatusProps): UseUpdateRouteStatusResponseProps => {
  const queryClient = useQueryClient();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { routeDispatch } = useRouteContext();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Route, unknown, { status: RouteStatus }>(
    ({ status }: { status: RouteStatus }): Promise<Route> => {
      return routeService.updateStatus({
        status,
        route: driverRoute,
      });
    },
    {
      onSuccess: ({ status }) => {
        const key = getRouteMutationKey({ routeId: driverRoute.id });

        const previousQueryContent = queryClient.getQueryData<Route>(key);

        if (!previousQueryContent) {
          throw new Error(`previousQueryContent does not exist`);
        }

        previousQueryContent.status = status;

        queryClient.setQueryData<Route>(key, previousQueryContent);

        // Based on the route modification, change the specificStops within it.
        if (driverRoute.getRouteStartStop() && driverRoute.status !== ROUTE_STATUS.started) {
          driverRoute.getRouteStartStop()!.status = STOP_STATUS.completed;
        }
        if (
          driverRoute.getRouteEndStop() &&
          driverRoute.status === ROUTE_STATUS.finished &&
          driverRoute.getNumberPendingStops() === 0
        ) {
          driverRoute.getRouteEndStop()!.status = STOP_STATUS.completed;
        }

        // TODO: Review if we should use queryClient.setQueryData for updating the search routes list.
        routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: driverRoute });

        // Invalidates the search unfinished routes list and the last unfinished route.
        queryClient.invalidateQueries([...getUnfinishedRoutesMutationKey(), ...getLastUnfinishedRouteMutationKey()]);
      },
      // If the mutation fails, use the context we returned above
      onError: (err: any, _variables: any, context: any) => {
        console.info(`Create Survey Error:`, err);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: ({ status }: { status: RouteStatus }) => {
      mutate({ status });
    },
  };
};
